/**
 * Customer auth token management.
 *
 * Storage strategy:
 *   - rememberMe = true  (default) → localStorage  (persists across browser restarts)
 *   - rememberMe = false            → sessionStorage (cleared when tab closes)
 *
 * Keys used in both stores:
 *   map_customer_token    — JWT access token
 *   map_customer_refresh  — opaque refresh token
 *   map_customer_user     — JSON-serialised CustomerUser
 *
 * The remember-me preference itself is stored in localStorage so the next page
 * load knows which store the tokens are in.
 */

const KEY_TOKEN = 'map_customer_token';
const KEY_REFRESH = 'map_customer_refresh';
const KEY_USER = 'map_customer_user';
const KEY_REMEMBER = 'map_customer_remember';

export interface CustomerUser {
  sub: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  tenantId: string;
  role: 'customer';
  exp?: number;
  iat?: number;
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  customerId: string;
}

// ─── Storage helpers ──────────────────────────────────────────────────────────

function getStore(): Storage {
  const remember = localStorage.getItem(KEY_REMEMBER);
  // Default to localStorage (remember = true) unless explicitly set to 'false'
  return remember === 'false' ? sessionStorage : localStorage;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function getToken(): string | null {
  // Check both stores so the interceptor works regardless of which store is active
  return sessionStorage.getItem(KEY_TOKEN) ?? localStorage.getItem(KEY_TOKEN);
}

export function getRefreshToken(): string | null {
  return sessionStorage.getItem(KEY_REFRESH) ?? localStorage.getItem(KEY_REFRESH);
}

export function setTokenPair(pair: TokenPair, rememberMe = true): void {
  // Persist the preference
  localStorage.setItem(KEY_REMEMBER, rememberMe ? 'true' : 'false');

  const store = rememberMe ? localStorage : sessionStorage;
  const oppositeStore = rememberMe ? sessionStorage : localStorage;

  // Clear from the other store to avoid stale tokens in both places
  oppositeStore.removeItem(KEY_TOKEN);
  oppositeStore.removeItem(KEY_REFRESH);
  oppositeStore.removeItem(KEY_USER);

  store.setItem(KEY_TOKEN, pair.accessToken);
  store.setItem(KEY_REFRESH, pair.refreshToken);

  // Parse and cache the user from the JWT payload
  const user = parseJwt(pair.accessToken);
  if (user) {
    store.setItem(KEY_USER, JSON.stringify(user));
  }
}

export function clearAuth(): void {
  [localStorage, sessionStorage].forEach((s) => {
    s.removeItem(KEY_TOKEN);
    s.removeItem(KEY_REFRESH);
    s.removeItem(KEY_USER);
  });
  // Keep KEY_REMEMBER so the next login pre-fills the checkbox correctly
}

export function getCurrentCustomer(): CustomerUser | null {
  const raw = sessionStorage.getItem(KEY_USER) ?? localStorage.getItem(KEY_USER);
  if (raw) {
    try {
      return JSON.parse(raw) as CustomerUser;
    } catch {
      // fall through
    }
  }
  // Fallback: parse from the access token directly
  const token = getToken();
  if (!token) return null;
  return parseJwt(token);
}

export function isTokenExpired(): boolean {
  const user = getCurrentCustomer();
  if (!user?.exp) return true;
  // Consider expired if within 30 seconds of actual expiry
  return user.exp * 1000 < Date.now() + 30_000;
}

/** Whether the user previously chose to be remembered. Defaults to true. */
export function getRememberMe(): boolean {
  return localStorage.getItem(KEY_REMEMBER) !== 'false';
}

// ─── Private helpers ──────────────────────────────────────────────────────────

function parseJwt(token: string): CustomerUser | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as CustomerUser;
  } catch {
    return null;
  }
}
